import { useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Icon } from '../components/Icon'
import { loadSearchIndex, searchConfidence, searchRecords, type SearchResult } from '../lib/search'

export function SearchPage() {
  const [params, setParams] = useSearchParams()
  const query = params.get('q') ?? ''
  const [draft, setDraft] = useState(query)
  const [results, setResults] = useState<SearchResult[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => setDraft(query), [query])
  useEffect(() => {
    let active = true
    if (!query.trim()) {
      setResults([])
      return
    }
    setLoading(true)
    setError('')
    void loadSearchIndex()
      .then((records) => active && setResults(searchRecords(records, query)))
      .catch((reason) => active && setError(reason instanceof Error ? reason.message : 'Search could not be loaded.'))
      .finally(() => active && setLoading(false))
    return () => {
      active = false
    }
  }, [query])

  const confidence = useMemo(() => searchConfidence(results), [results])
  return (
    <div className="page-width page-stack search-page">
      <section className="search-hero">
        <span className="eyebrow">Search all 105 modules and every section</span>
        <h1>Find the answer even if you do not know the exact term</h1>
        <p>Search understands partial words, common misspellings, abbreviations, and related terms. AI can separately explain what a broader question means.</p>
        <form className="search-form" onSubmit={(event) => {
          event.preventDefault()
          const value = draft.trim()
          setParams(value ? { q: value } : {})
        }}>
          <Icon name="search" />
          <input autoFocus value={draft} onChange={(event) => setDraft(event.target.value)} placeholder="Try: spoucal benifits, disabled child, Roth conversion…" aria-label="Search the course" />
          <button className="button primary" type="submit">Search</button>
        </form>
        <div className="search-examples">
          <span>Examples:</span>
          {['spoucal benifits', 'money for disabled child', 'two bucket retirement', 'state estate tax'].map((example) => <button type="button" key={example} onClick={() => setParams({ q: example })}>{example}</button>)}
        </div>
      </section>
      {loading ? <div className="loading-card">Searching the course…</div> : null}
      {error ? <div className="notice error">{error}</div> : null}
      {query && !loading ? (
        <section className="search-results">
          <div className="search-summary">
            <div><span className="eyebrow">{results.length ? `${results.length} relevant sections` : 'No strong local match'}</span><h2>Results for “{query}”</h2></div>
            <Link className="button ai-button" to={`/ask?intent=semantic_search&q=${encodeURIComponent(query)}`}><Icon name="ai" size={17} />Interpret with AI</Link>
          </div>
          {confidence !== 'high' ? <div className="notice info"><strong>Want broader interpretation?</strong> AI can explain the meaning of a plain-language question after sign-in. The course results themselves still come from the local, account-free search in this browser.</div> : null}
          <div className="result-list">
            {results.map((result) => (
              <Link key={`${result.moduleId}:${result.sectionId}`} className="search-result" to={result.url}>
                <span className="result-code">{result.moduleId}</span>
                <span className="result-copy"><small>{result.courseTitle}</small><strong>{result.moduleTitle}</strong><b>{result.sectionLabel} · {result.heading}</b><p>{result.preview}</p></span>
                <Icon name="chevron" />
              </Link>
            ))}
            {!results.length ? <div className="empty-state"><Icon name="search" size={30} /><h3>Try a broader phrase</h3><p>Use a related term, fewer words, or choose Interpret with AI to clarify the question.</p></div> : null}
          </div>
        </section>
      ) : null}
    </div>
  )
}
